/**
 * Capacitor Service
 *
 * Servizio centralizzato per la gestione delle funzionalità native
 * tramite Capacitor 8. Sostituisce le vecchie API Cordova.
 */

import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { Network } from '@capacitor/network';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Geolocation } from '@capacitor/geolocation';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { PushNotifications } from '@capacitor/push-notifications';

class CapacitorService {
  constructor() {
    this._isNative = Capacitor.isNativePlatform();
    this._platform = Capacitor.getPlatform();
    this._deviceInfo = null;
    this._networkStatus = null;
    this._appStateListeners = [];
    this._networkListeners = [];
    this._pushNotificationListeners = [];
  }

  /**
   * Verifica se l'app è in esecuzione su piattaforma nativa
   */
  get isNative() {
    return this._isNative;
  }

  /**
   * Ottiene la piattaforma corrente (web, ios, android)
   */
  get platform() {
    return this._platform;
  }

  /**
   * Verifica se l'app è in esecuzione su iOS
   */
  get isIOS() {
    return this._platform === 'ios';
  }

  /**
   * Verifica se l'app è in esecuzione su Android
   */
  get isAndroid() {
    return this._platform === 'android';
  }

  /**
   * Verifica se l'app è in esecuzione su web
   */
  get isWeb() {
    return this._platform === 'web';
  }

  // ============================================
  // DEVICE
  // ============================================

  /**
   * Ottiene le informazioni del dispositivo
   */
  async getDeviceInfo() {
    if (!this._isNative) {
      return {
        platform: 'web',
        model: navigator.userAgent,
        operatingSystem: navigator.platform,
        osVersion: navigator.appVersion,
        manufacturer: 'unknown',
        isVirtual: false,
        webViewVersion: navigator.appVersion,
      };
    }

    if (!this._deviceInfo) {
      this._deviceInfo = await Device.getInfo();
    }
    return this._deviceInfo;
  }

  /**
   * Ottiene l'ID univoco del dispositivo
   */
  async getDeviceId() {
    if (!this._isNative) {
      // Per web, genera un ID basato su localStorage
      let deviceId = localStorage.getItem('device_id');
      if (!deviceId) {
        deviceId = 'web_' + Math.random().toString(36).substring(2, 15);
        localStorage.setItem('device_id', deviceId);
      }
      return { identifier: deviceId };
    }
    return await Device.getId();
  }

  // ============================================
  // STATUS BAR
  // ============================================

  /**
   * Imposta il colore di sfondo della status bar
   */
  async setStatusBarColor(hexColor) {
    if (!this._isNative) return;

    try {
      await StatusBar.setBackgroundColor({ color: hexColor });
    } catch (error) {
      console.warn('StatusBar.setBackgroundColor non supportato:', error);
    }
  }

  /**
   * Imposta lo stile della status bar (light/dark)
   */
  async setStatusBarStyle(style = 'dark') {
    if (!this._isNative) return;

    try {
      const statusBarStyle = style === 'light' ? Style.Light : Style.Dark;
      await StatusBar.setStyle({ style: statusBarStyle });
    } catch (error) {
      console.warn('StatusBar.setStyle non supportato:', error);
    }
  }

  /**
   * Mostra la status bar
   */
  async showStatusBar() {
    if (!this._isNative) return;

    try {
      await StatusBar.show();
    } catch (error) {
      console.warn('StatusBar.show non supportato:', error);
    }
  }

  /**
   * Nasconde la status bar
   */
  async hideStatusBar() {
    if (!this._isNative) return;

    try {
      await StatusBar.hide();
    } catch (error) {
      console.warn('StatusBar.hide non supportato:', error);
    }
  }

  /**
   * Imposta se la status bar si sovrappone al webview
   */
  async setStatusBarOverlay(overlay = false) {
    if (!this._isNative) return;

    try {
      await StatusBar.setOverlaysWebView({ overlay });
    } catch (error) {
      console.warn('StatusBar.setOverlaysWebView non supportato:', error);
    }
  }

  // ============================================
  // APP LIFECYCLE
  // ============================================

  /**
   * Aggiunge un listener per lo stato dell'app (foreground/background)
   */
  addAppStateListener(callback) {
    if (!this._isNative) return null;

    const listener = App.addListener('appStateChange', (state) => {
      callback(state.isActive);
    });
    this._appStateListeners.push(listener);
    return listener;
  }

  /**
   * Aggiunge un listener per il pulsante back (Android)
   */
  addBackButtonListener(callback) {
    if (!this._isNative || !this.isAndroid) return null;

    return App.addListener('backButton', callback);
  }

  /**
   * Aggiunge un listener per la gestione degli URL
   */
  addAppUrlOpenListener(callback) {
    if (!this._isNative) return null;

    return App.addListener('appUrlOpen', callback);
  }

  /**
   * Esce dall'app (solo Android)
   */
  async exitApp() {
    if (!this._isNative || !this.isAndroid) return;
    await App.exitApp();
  }

  // ============================================
  // NETWORK
  // ============================================

  /**
   * Ottiene lo stato attuale della rete
   */
  async getNetworkStatus() {
    try {
      this._networkStatus = await Network.getStatus();
      return this._networkStatus;
    } catch (error) {
      console.warn('Network.getStatus errore:', error);
      return { connected: navigator.onLine, connectionType: 'unknown' };
    }
  }

  /**
   * Aggiunge un listener per i cambiamenti di rete
   */
  addNetworkListener(callback) {
    const listener = Network.addListener('networkStatusChange', (status) => {
      this._networkStatus = status;
      callback(status);
    });
    this._networkListeners.push(listener);
    return listener;
  }

  /**
   * Verifica se il dispositivo è online
   */
  async isOnline() {
    const status = await this.getNetworkStatus();
    return status.connected;
  }

  // ============================================
  // CAMERA
  // ============================================

  /**
   * Scatta una foto con la fotocamera
   */
  async takePhoto(options = {}) {
    const defaultOptions = {
      quality: 80,
      allowEditing: true,
      resultType: CameraResultType.Base64,
      source: CameraSource.Camera,
      width: 1024,
      height: 1024,
      correctOrientation: true,
    };

    try {
      const image = await Camera.getPhoto({ ...defaultOptions, ...options });
      return {
        success: true,
        data: image.base64String ? `data:image/${image.format};base64,${image.base64String}` : image.webPath,
        format: image.format,
      };
    } catch (error) {
      console.warn('Camera.getPhoto errore:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Seleziona una foto dalla galleria
   */
  async pickPhoto(options = {}) {
    return this.takePhoto({
      ...options,
      source: CameraSource.Photos,
    });
  }

  /**
   * Mostra prompt per scegliere tra camera e galleria
   */
  async getPhotoWithPrompt(options = {}) {
    return this.takePhoto({
      ...options,
      source: CameraSource.Prompt,
    });
  }

  /**
   * Verifica i permessi della camera
   */
  async checkCameraPermissions() {
    try {
      const permissions = await Camera.checkPermissions();
      return permissions;
    } catch (error) {
      console.warn('Camera.checkPermissions errore:', error);
      return { camera: 'denied', photos: 'denied' };
    }
  }

  /**
   * Richiede i permessi della camera
   */
  async requestCameraPermissions() {
    try {
      const permissions = await Camera.requestPermissions();
      return permissions;
    } catch (error) {
      console.warn('Camera.requestPermissions errore:', error);
      return { camera: 'denied', photos: 'denied' };
    }
  }

  // ============================================
  // GEOLOCATION
  // ============================================

  /**
   * Ottiene la posizione corrente
   */
  async getCurrentPosition(options = {}) {
    const defaultOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    };

    try {
      const position = await Geolocation.getCurrentPosition({ ...defaultOptions, ...options });
      return {
        success: true,
        coords: {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          altitude: position.coords.altitude,
          altitudeAccuracy: position.coords.altitudeAccuracy,
          heading: position.coords.heading,
          speed: position.coords.speed,
        },
        timestamp: position.timestamp,
      };
    } catch (error) {
      console.warn('Geolocation.getCurrentPosition errore:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Osserva la posizione in tempo reale
   */
  async watchPosition(callback, options = {}) {
    const defaultOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    };

    try {
      const watchId = await Geolocation.watchPosition({ ...defaultOptions, ...options }, (position, err) => {
        if (err) {
          callback({ success: false, error: err.message });
          return;
        }
        callback({
          success: true,
          coords: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          },
          timestamp: position.timestamp,
        });
      });
      return watchId;
    } catch (error) {
      console.warn('Geolocation.watchPosition errore:', error);
      return null;
    }
  }

  /**
   * Ferma l'osservazione della posizione
   */
  async clearWatch(watchId) {
    if (watchId) {
      await Geolocation.clearWatch({ id: watchId });
    }
  }

  /**
   * Verifica i permessi di geolocalizzazione
   */
  async checkGeolocationPermissions() {
    try {
      const permissions = await Geolocation.checkPermissions();
      return permissions;
    } catch (error) {
      console.warn('Geolocation.checkPermissions errore:', error);
      return { location: 'denied', coarseLocation: 'denied' };
    }
  }

  /**
   * Richiede i permessi di geolocalizzazione
   */
  async requestGeolocationPermissions() {
    try {
      const permissions = await Geolocation.requestPermissions();
      return permissions;
    } catch (error) {
      console.warn('Geolocation.requestPermissions errore:', error);
      return { location: 'denied', coarseLocation: 'denied' };
    }
  }

  // ============================================
  // FILESYSTEM
  // ============================================

  /**
   * Legge un file
   */
  async readFile(path, directory = Directory.Documents) {
    try {
      const result = await Filesystem.readFile({
        path,
        directory,
      });
      return { success: true, data: result.data };
    } catch (error) {
      console.warn('Filesystem.readFile errore:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Scrive un file
   */
  async writeFile(path, data, directory = Directory.Documents) {
    try {
      await Filesystem.writeFile({
        path,
        data,
        directory,
      });
      return { success: true };
    } catch (error) {
      console.warn('Filesystem.writeFile errore:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Elimina un file
   */
  async deleteFile(path, directory = Directory.Documents) {
    try {
      await Filesystem.deleteFile({
        path,
        directory,
      });
      return { success: true };
    } catch (error) {
      console.warn('Filesystem.deleteFile errore:', error);
      return { success: false, error: error.message };
    }
  }

  // ============================================
  // PUSH NOTIFICATIONS (for permission management)
  // ============================================

  /**
   * Verifica i permessi delle notifiche push
   */
  async checkPushNotificationPermissions() {
    if (!this._isNative) {
      // Per web, usa l'API Notification
      if ('Notification' in window) {
        return { receive: Notification.permission === 'granted' ? 'granted' : 'denied' };
      }
      return { receive: 'denied' };
    }

    try {
      const result = await PushNotifications.checkPermissions();
      return result;
    } catch (error) {
      console.warn('PushNotifications.checkPermissions errore:', error);
      return { receive: 'denied' };
    }
  }

  /**
   * Richiede i permessi delle notifiche push
   */
  async requestPushNotificationPermissions() {
    if (!this._isNative) {
      // Per web, usa l'API Notification
      if ('Notification' in window) {
        const permission = await Notification.requestPermission();
        return { receive: permission === 'granted' ? 'granted' : 'denied' };
      }
      return { receive: 'denied' };
    }

    try {
      const result = await PushNotifications.requestPermissions();
      return result;
    } catch (error) {
      console.warn('PushNotifications.requestPermissions errore:', error);
      return { receive: 'denied' };
    }
  }

  /**
   * Registra il dispositivo per le notifiche push
   */
  async registerPushNotifications() {
    if (!this._isNative) return;

    try {
      await PushNotifications.register();
    } catch (error) {
      console.warn('PushNotifications.register errore:', error);
    }
  }

  /**
   * Aggiunge listener per le notifiche push
   */
  addPushNotificationListeners(callbacks = {}) {
    if (!this._isNative) return;

    const listeners = [];

    if (callbacks.onRegistration) {
      listeners.push(
        PushNotifications.addListener('registration', callbacks.onRegistration)
      );
    }

    if (callbacks.onRegistrationError) {
      listeners.push(
        PushNotifications.addListener('registrationError', callbacks.onRegistrationError)
      );
    }

    if (callbacks.onPushNotificationReceived) {
      listeners.push(
        PushNotifications.addListener('pushNotificationReceived', callbacks.onPushNotificationReceived)
      );
    }

    if (callbacks.onPushNotificationActionPerformed) {
      listeners.push(
        PushNotifications.addListener('pushNotificationActionPerformed', callbacks.onPushNotificationActionPerformed)
      );
    }

    this._pushNotificationListeners.push(...listeners);
    return listeners;
  }

  // ============================================
  // CLEANUP
  // ============================================

  /**
   * Rimuove tutti i listener
   */
  async removeAllListeners() {
    // Remove app state listeners
    for (const listener of this._appStateListeners) {
      if (listener && listener.remove) {
        await listener.remove();
      }
    }
    this._appStateListeners = [];

    // Remove network listeners
    for (const listener of this._networkListeners) {
      if (listener && listener.remove) {
        await listener.remove();
      }
    }
    this._networkListeners = [];

    // Remove push notification listeners
    for (const listener of this._pushNotificationListeners) {
      if (listener && listener.remove) {
        await listener.remove();
      }
    }
    this._pushNotificationListeners = [];
  }
}

// Esporta un'istanza singleton
const capacitorService = new CapacitorService();
export default capacitorService;

// Esporta anche la classe per testing
export { CapacitorService };
